import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CustomerMeasurementSummary,
  MeasurementAttemptOutcome,
  MeasurementQrCode,
  MeasurementSessionSummary,
  MeasurementSettingsSummary,
  PdType,
} from '@optiflow/types';
import type { CaptureCandidate } from '@/modules/capture/captureClient';
import { api } from '@/services/apiClient';
import { useIdempotentMutation } from '@/services/useIdempotentMutation';

/** The desk sends the same body a phone does; only the route and its authentication differ. */
export type DeviceCandidate = CaptureCandidate;

export const measurementKeys = {
  all: ['measurements'] as const,
  forCustomer: (customerId: string, includeVoided: boolean) =>
    [...measurementKeys.all, 'customer', customerId, includeVoided] as const,
  settings: () => [...measurementKeys.all, 'settings'] as const,
  session: (sessionId: string) => [...measurementKeys.all, 'session', sessionId] as const,
};

export function useCustomerMeasurementsQuery(
  customerId: string | undefined,
  includeVoided = false,
) {
  return useQuery({
    queryKey: measurementKeys.forCustomer(customerId ?? '', includeVoided),
    queryFn: () =>
      api.get<CustomerMeasurementSummary[]>(`/customers/${customerId ?? ''}/measurements`, {
        params: includeVoided ? { includeVoided: true } : {},
      }),
    enabled: Boolean(customerId),
  });
}

/** Millimetres are decimal strings the whole way. Nothing here turns one into a float. */
export interface MeasurementInput {
  rightPdMm?: string;
  leftPdMm?: string;
  totalPdMm?: string;
  /** Which PD the values are. Sent with any PD value, never without one. */
  pdType?: PdType;
  faceWidthMm?: string;
  bridgeWidthMm?: string;
  eyeWidthMm?: string;
  faceLengthMm?: string;
  notes?: string;
}

export function useRecordMeasurementMutation(customerId: string) {
  const queryClient = useQueryClient();

  return useIdempotentMutation<CustomerMeasurementSummary, MeasurementInput>(
    (payload, config) =>
      api.post<CustomerMeasurementSummary>(
        `/customers/${customerId}/measurements`,
        payload,
        config,
      ),
    { onSuccess: () => void queryClient.invalidateQueries({ queryKey: measurementKeys.all }) },
  );
}

export function useVoidMeasurementMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post<CustomerMeasurementSummary>(`/measurements/${id}/void`, { reason }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: measurementKeys.all }),
  });
}

/** Camera mode and time limits. Camera flows appear only when the mode is not OFF. */
export function useMeasurementSettingsQuery() {
  return useQuery({
    queryKey: measurementKeys.settings(),
    queryFn: () => api.get<MeasurementSettingsSummary>('/measurement-settings'),
    staleTime: 60_000,
  });
}

export function useCreateMeasurementSessionMutation() {
  return useIdempotentMutation<
    MeasurementSessionSummary,
    { customerId: string; requestedSource: 'LIVE_CAMERA' | 'PHONE_QR'; orderId?: string }
  >((payload, config) =>
    api.post<MeasurementSessionSummary>('/measurements/sessions', payload, config),
  );
}

/** Each call voids the code before it, so it is a plain mutation rather than an idempotent one. */
export function useIssueQrCodeMutation() {
  return useMutation({
    mutationFn: (sessionId: string) =>
      api.post<MeasurementQrCode>(`/measurements/sessions/${sessionId}/qr-token`),
  });
}

/** The session as the desk sees it. Polled while open until the stream replaces polling. */
export function useMeasurementSessionQuery(sessionId: string | null, pollMs: number | false) {
  return useQuery({
    queryKey: measurementKeys.session(sessionId ?? ''),
    queryFn: () => api.get<MeasurementSessionSummary>(`/measurements/sessions/${sessionId ?? ''}`),
    enabled: Boolean(sessionId),
    refetchInterval: pollMs,
  });
}

export function useCancelMeasurementSessionMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionId: string) =>
      api.post<MeasurementSessionSummary>(`/measurements/sessions/${sessionId}/cancel`),
    onSuccess: (session) => queryClient.setQueryData(measurementKeys.session(session.id), session),
  });
}

/**
 * The desk accepts the estimate. It becomes a measurement in the customer's history — marked
 * reference-only when the calibration says so, which is what keeps it off a prescription.
 */
export function useAcceptMeasurementSessionMutation() {
  const queryClient = useQueryClient();

  return useIdempotentMutation<MeasurementSessionSummary, { sessionId: string }>(
    ({ sessionId }, config) =>
      api.post<MeasurementSessionSummary>(
        `/measurements/sessions/${sessionId}/accept`,
        undefined,
        config,
      ),
    {
      onSuccess: (session) => {
        queryClient.setQueryData(measurementKeys.session(session.id), session);
        void queryClient.invalidateQueries({ queryKey: measurementKeys.all });
      },
    },
  );
}

/** The desk asks for another try. The candidate and its quality go; the capture window starts again. */
export function useRetakeMeasurementSessionMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionId: string) =>
      api.post<MeasurementSessionSummary>(`/measurements/sessions/${sessionId}/retake`),
    onSuccess: (session) => queryClient.setQueryData(measurementKeys.session(session.id), session),
  });
}

/** This device's own camera begins capturing. A phone session starts by redeeming its code instead. */
export function useStartDeviceCaptureMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionId: string) =>
      api.post<MeasurementSessionSummary>(`/measurements/sessions/${sessionId}/start`),
    onSuccess: (session) => queryClient.setQueryData(measurementKeys.session(session.id), session),
  });
}

/** The desk offers its own camera's result for review. Idempotent by submissionId, so a lost answer can be retried. */
export function useSubmitDeviceCandidateMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sessionId, candidate }: { sessionId: string; candidate: DeviceCandidate }) =>
      api.post<MeasurementSessionSummary>(
        `/measurements/sessions/${sessionId}/candidate`,
        candidate,
      ),
    onSuccess: (session) => queryClient.setQueryData(measurementKeys.session(session.id), session),
  });
}

/** An attempt that produced no number. Counted with its reason; the reading that failed is never sent. */
export function useRecordAttemptMutation() {
  return useMutation({
    mutationFn: ({
      sessionId,
      outcome,
    }: {
      sessionId: string;
      outcome: MeasurementAttemptOutcome;
    }) =>
      api.post<MeasurementSessionSummary>(`/measurements/sessions/${sessionId}/attempts`, {
        outcome,
      }),
  });
}
